import { initializeApp } from "firebase/app";
import {
  getFirestore, doc, onSnapshot, setDoc, getDoc,
  initializeFirestore, persistentLocalCache, persistentMultipleTabManager
} from "firebase/firestore";

const firebaseConfig = {
  apiKey: "AIzaSyDETIguZFEBaoN1piDp9YIiiwx6tFFQX0c",
  authDomain: "accounting-center-f2adb.firebaseapp.com",
  projectId: "accounting-center-f2adb",
  storageBucket: "accounting-center-f2adb.firebasestorage.app",
  messagingSenderId: "66659637098",
  appId: "1:66659637098:web:2acf3a9df8d89e24cdb713"
};

const app = initializeApp(firebaseConfig);
let db;
try {
  db = initializeFirestore(app, {
    localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() })
  });
} catch(e) { db = getFirestore(app); }
export { db };

const MAIN_REF  = () => doc(db, "center", "main");
const LOGOS_REF = () => doc(db, "center", "logos"); // مستند منفصل للوجوهات

const LS_MAIN   = "acc_v7_main";
const LS_LOGOS  = "acc_v7_logos";
const LS_DIRTY  = "acc_v7_dirty";

const lsGet  = k => { try { const v=localStorage.getItem(k); return v?JSON.parse(v):null; } catch { return null; } };
const lsSet  = (k,v) => { try { localStorage.setItem(k,JSON.stringify(v)); } catch {} };
const lsDel  = k => { try { localStorage.removeItem(k); } catch {} };

/* ══ Logo compression ══ */
export async function compressImage(base64, maxPx=200, quality=0.75) {
  return new Promise(resolve => {
    try {
      const img = new window.Image();
      img.onload = () => {
        let w=img.width, h=img.height;
        if(w>maxPx||h>maxPx){ if(w>h){h=Math.round(h*maxPx/w);w=maxPx;}else{w=Math.round(w*maxPx/h);h=maxPx;} }
        const c=document.createElement("canvas"); c.width=w; c.height=h;
        c.getContext("2d").drawImage(img,0,0,w,h);
        resolve(c.toDataURL("image/jpeg",quality));
      };
      img.onerror = ()=>resolve(base64);
      img.src = base64;
    } catch { resolve(base64); }
  });
}

export async function uploadLogoToStorage(base64) {
  try { return await compressImage(base64, 200, 0.75); } catch { return base64; }
}

/* ══ Split: نفصل اللوجوهات عن البيانات الرئيسية ══ */
function extractLogos(data) {
  const logos = {};
  const clean = { ...data };
  if (data.centerLogo?.startsWith("data:")) {
    logos.centerLogo = data.centerLogo;
    delete clean.centerLogo;
  }
  if (data.courses) {
    clean.courses = data.courses.map(c => {
      if (c.logo?.startsWith("data:")) {
        logos[`c_${c.id}`] = c.logo;
        return { ...c, logo: "" };
      }
      return c;
    });
  }
  return { clean, logos };
}

function injectLogos(data, logos) {
  if (!logos || !Object.keys(logos).length) return data;
  const out = { ...data };
  if (logos.centerLogo) out.centerLogo = logos.centerLogo;
  if (out.courses) {
    out.courses = out.courses.map(c => ({
      ...c, logo: logos[`c_${c.id}`] || c.logo || ""
    }));
  }
  return out;
}

/* ══ Save guard ══ */
let _lastSaveAt = 0;
const COOLDOWN = 3000;
let _timer = null;
let _pending = null;

async function flushNow(data) {
  try {
    const { clean, logos } = extractLogos(data);
    // حفظ البيانات الرئيسية
    await setDoc(MAIN_REF(), clean, { merge: true });
    // حفظ اللوجوهات في مستند منفصل
    if (Object.keys(logos).length > 0) {
      lsSet(LS_LOGOS, logos);
      let prevLogos = {};
      try { const s=await getDoc(LOGOS_REF()); if(s.exists()) prevLogos=s.data(); } catch {}
      await setDoc(LOGOS_REF(), { ...prevLogos, ...logos });
    }
    lsDel(LS_DIRTY);
    return true;
  } catch(e) {
    console.warn("Firebase flush failed:", e.message);
    return false;
  }
}

/* beforeunload + visibilitychange — نحفظ قبل الإغلاق */
if (typeof window !== "undefined") {
  const saveOnExit = () => {
    if (_pending) { lsSet(LS_DIRTY, _pending); flushNow(_pending); }
  };
  window.addEventListener("beforeunload", saveOnExit);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden" && _pending) saveOnExit();
    if (document.visibilityState === "visible") {
      const dirty = lsGet(LS_DIRTY);
      if (dirty) flushNow(dirty).then(ok => { if(ok) lsDel(LS_DIRTY); });
    }
  });
}

/* ══ PUBLIC API ══ */
export async function loadFromFirebase(defaultDB) {
  const base = typeof defaultDB === "function" ? defaultDB() : (defaultDB || {});
  const dirty = lsGet(LS_DIRTY);
  try {
    const [mainSnap, logoSnap] = await Promise.all([
      getDoc(MAIN_REF()),
      getDoc(LOGOS_REF()).catch(() => null)
    ]);
    if (mainSnap.exists()) {
      let data = { ...base, ...mainSnap.data() };
      // دمج اللوجوهات: Firestore أولاً، ثم localStorage كـ fallback
      const remoteLogos = logoSnap?.exists() ? logoSnap.data() : {};
      const localLogos  = lsGet(LS_LOGOS) || {};
      const logos = { ...localLogos, ...remoteLogos }; // Firestore يغلب المحلي
      data = injectLogos(data, logos);
      // لو في dirty data، ادمجها فوق
      if (dirty) {
        data = { ...data, ...dirty };
        flushNow(data).then(ok => { if(ok) lsDel(LS_DIRTY); });
      }
      lsSet(LS_MAIN, data);
      return data;
    } else {
      if (dirty) {
        await flushNow(dirty);
        return { ...base, ...dirty };
      }
      return base;
    }
  } catch(e) {
    console.warn("loadFromFirebase offline:", e.message);
    const cached = lsGet(LS_MAIN);
    const logos  = lsGet(LS_LOGOS) || {};
    let data = cached ? { ...base, ...cached } : base;
    data = injectLogos(data, logos);
    if (dirty) data = { ...data, ...dirty };
    return data;
  }
}

export function saveToFirebase(data) {
  // 1. حفظ فوري محلي
  lsSet(LS_MAIN, data);
  _pending = data;
  lsSet(LS_DIRTY, data);
  // 2. debounced Firestore save
  clearTimeout(_timer);
  _timer = setTimeout(async () => {
    _lastSaveAt = Date.now();
    const ok = await flushNow(data);
    if (ok) _pending = null;
  }, 600);
}

export function subscribeToFirebase(callback) {
  return onSnapshot(
    MAIN_REF(),
    async (snap) => {
      if (Date.now() - _lastSaveAt < COOLDOWN) return;
      if (!snap.exists()) return;
      let raw = snap.data();
      const localLogos  = lsGet(LS_LOGOS) || {};
      let logoSnap = null;
      try { logoSnap = await getDoc(LOGOS_REF()); } catch {}
      const remoteLogos = logoSnap?.exists() ? logoSnap.data() : {};
      const logos = { ...localLogos, ...remoteLogos };
      raw = injectLogos(raw, logos);
      lsSet(LS_MAIN, raw);
      callback(raw);
    },
    e => console.warn("Snapshot error:", e.message)
  );
}
